import i18n from 'i18n'
import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit'
import { customersAPI, type CustomerRow } from '../api/customers'
import { isOfflineQueuedFulfilledAction } from 'helpers/offlineAction'
import { getLastKnownOnline } from 'utils/connectivityCheck'
import { branchChanged } from './branchSlice'

interface Customer extends CustomerRow {}

interface CustomersState {
 data: Customer[]
 loading: boolean
 error: string | null
 pagination: {
 total: number
 per_page: number
 current_page: number
 last_page: number
 } | null
}

const initialState: CustomersState = {
 data: [],
 loading: false,
 error: null,
 pagination: null,
}

export const fetchCustomers = createAsyncThunk(
 'customers/fetchCustomers',
 async (params: any = {}, { rejectWithValue }) => {
 try {
 const response = await customersAPI.getAll(params)
 return response
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.customers.fetchFailed'))
 }
 }
)

export const createCustomer = createAsyncThunk(
 'customers/createCustomer',
 async (customerData: any, { rejectWithValue }) => {
 if (!getLastKnownOnline()) {
 return { ...customerData, _offlineOptimistic: true }
 }
 try {
 const response = await customersAPI.create(customerData)
 return response.data
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.errors || error.response?.data?.message || i18n.t('errors.customers.createFailed'))
 }
 }
)

export const updateCustomer = createAsyncThunk(
 'customers/updateCustomer',
 async ({ id, data }: { id: number; data: any }, { rejectWithValue }) => {
 try {
 const response = await customersAPI.update(id, data)
 return { id, ...(response.data as any) }
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.errors || error.response?.data?.message || i18n.t('errors.customers.updateFailed'))
 }
 }
)

export const deleteCustomer = createAsyncThunk(
 'customers/deleteCustomer',
 async (id: number, { rejectWithValue }) => {
 try {
 await customersAPI.delete(id)
 return id
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.customers.deleteFailed'))
 }
 }
)

export const addCustomerBalance = createAsyncThunk(
 'customers/addBalance',
 async ({ id, amount, type, notes }: any, { rejectWithValue }) => {
 try {
 const { customersAPI } = await import('../api/customers')
 const response = await customersAPI.addBalance(id, amount, type, notes)
 return response.data
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.customers.addBalanceFailed'))
 }
 }
)

const customersSlice = createSlice({
 name: 'customers',
 initialState,
 reducers: {
 clearCustomers: (state) => {
 state.data = []
 state.error = null
 state.pagination = null
 },
 /** Merge a customer created or loaded from POS without replacing the full list */
 mergePosCustomer: (state, action: PayloadAction<Customer>) => {
 const c = action.payload
 if (!c?.id) return
 if (!state.data.find((x) => x.id === c.id)) {
 state.data.unshift(c)
 }
 },
 },
 extraReducers: (builder) => {
 builder
 // Fetch customers
 .addCase(fetchCustomers.pending, (state) => {
 state.loading = true
 state.error = null
 })
 .addCase(fetchCustomers.fulfilled, (state, action) => {
 state.loading = false
 const payload = action.payload as any
 const data = payload?.data || payload?.result || payload
 state.data = Array.isArray(data) ? data.filter((item: any) => item != null) : []
 state.pagination = payload?.pagination || null
 })
 .addCase(fetchCustomers.rejected, (state, action) => {
 state.loading = false
 state.error = action.payload as string
 })
 // Create customer
 .addCase(createCustomer.pending, (state) => {
 state.loading = true
 state.error = null
 })
 .addCase(createCustomer.fulfilled, (state, action) => {
 state.loading = false
 if (isOfflineQueuedFulfilledAction(action)) {
 return
 }
 const created = action.payload as { id?: number; _offlineOptimistic?: boolean }
 if (created?._offlineOptimistic || created?.id == null) {
 return
 }
 state.data.unshift(created as any)
 })
 .addCase(createCustomer.rejected, (state, action) => {
 state.loading = false
 state.error = action.payload as string
 })
 // Update customer
 .addCase(updateCustomer.pending, (state) => {
 state.loading = true
 state.error = null
 })
 .addCase(updateCustomer.fulfilled, (state, action) => {
 state.loading = false
 if (isOfflineQueuedFulfilledAction(action)) {
 return
 }
 const index = state.data.findIndex((c) => c.id === (action.payload as any).id)
 if (index !== -1) {
 state.data[index] = action.payload as any
 }
 })
 .addCase(updateCustomer.rejected, (state, action) => {
 state.loading = false
 state.error = action.payload as string
 })
 // Delete customer
 .addCase(deleteCustomer.pending, (state) => {
 state.loading = true
 state.error = null
 })
 .addCase(deleteCustomer.fulfilled, (state, action) => {
 state.loading = false
 if (isOfflineQueuedFulfilledAction(action)) {
 return
 }
  const previousLength = state.data.length
 state.data = state.data.filter((c) => c.id !== action.payload)
  if (state.data.length !== previousLength && state.pagination) {
   const total = Math.max(0, state.pagination.total - 1)
   const perPage = Math.max(1, state.pagination.per_page)
   const lastPage = Math.max(1, Math.ceil(total / perPage))
   state.pagination = {
    ...state.pagination,
    total,
    last_page: lastPage,
    current_page: Math.min(state.pagination.current_page, lastPage),
   }
  }
 })
 .addCase(deleteCustomer.rejected, (state, action) => {
 state.loading = false
 state.error = action.payload as string
 })
 .addCase(branchChanged, (state) => {
 Object.assign(state, initialState)
 })
 },
})

export const { clearCustomers, mergePosCustomer } = customersSlice.actions
export default customersSlice.reducer
